import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import DispatchEditor from '@/components/dispatch/DispatchEditor'
import WikiItemSuggestions from '@/components/dashboard/WikiItemSuggestions'

function DispatchReview() {
  const location = useLocation()
  const navigate = useNavigate()
  
  const [subjectLine, setSubjectLine] = useState('')
  const [emailContent, setEmailContent] = useState('')
  const [wikiItems, setWikiItems] = useState([])
  const [selectedWikiItems, setSelectedWikiItems] = useState([])
  const [teamId, setTeamId] = useState(null)
  const [rawNotes, setRawNotes] = useState('')
  const [draftId, setDraftId] = useState(null)
  const [savingDraft, setSavingDraft] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState(null)
  const [formState, setFormState] = useState(null)

  useEffect(() => {
    // Get the generated dispatch from navigation state
    if (!location.state?.generatedDispatch) {
      navigate('/dashboard')
      return
    }

    const { generatedDispatch, teamId: tid, draftId: draftIdentifier, formState: fs } = location.state
    setSubjectLine(generatedDispatch.subject_line || '')
    setEmailContent(generatedDispatch.email_content || '')
    setWikiItems(generatedDispatch.wiki_items || [])
    setTeamId(tid)
    setRawNotes(generatedDispatch.raw_notes || '')
    setDraftId(draftIdentifier || null)
    setFormState(fs || null)
    
    // Pre-select all wiki items
    setSelectedWikiItems(generatedDispatch.wiki_items?.map((_, i) => i) || [])
  }, [location.state, navigate])

  const toggleWikiItem = (index) => {
    setSelectedWikiItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    )
  }

  const handleSaveDraft = async () => {
    if (!teamId) return

    setSavingDraft(true)
    try {
      const payload = {
        team_id: teamId,
        subject: subjectLine,
        content: emailContent,
        status: 'draft'
      }

      // If we have a draftId, update the existing draft, otherwise insert a new one
      if (draftId) {
        const { data, error } = await supabase
          .from('dispatch_drafts')
          .update(payload)
          .eq('id', draftId)
          .eq('team_id', teamId)
          .select()
          .single()

        if (error) throw error
        setLastSavedAt(new Date())
      } else {
        // Check if a draft already exists for this team
        const { data: existingDraft, error: fetchError } = await supabase
          .from('dispatch_drafts')
          .select('id')
          .eq('team_id', teamId)
          .eq('status', 'draft')
          .maybeSingle()

        if (fetchError) throw fetchError

        if (existingDraft) {
          // Update existing draft
          const { data, error } = await supabase
            .from('dispatch_drafts')
            .update(payload)
            .eq('id', existingDraft.id)
            .select()
            .single()

          if (error) throw error
          setDraftId(existingDraft.id)
        } else {
          // Insert new draft
          const { data, error } = await supabase
            .from('dispatch_drafts')
            .insert(payload)
            .select()
            .single()

          if (error) throw error
          if (data?.id) {
            setDraftId(data.id)
          }
        }
        setLastSavedAt(new Date())
      }
    } catch (error) {
      console.error('Failed to save draft:', error)
      alert(`Failed to save draft: ${error.message}`)
    } finally {
      setSavingDraft(false)
    }
  }

  const handleSendAndPublish = () => {
    // Navigate to a send confirmation or directly trigger send
    const selectedItems = wikiItems.filter((_, i) => selectedWikiItems.includes(i))
    
    navigate('/dispatch/send', {
      state: {
        subjectLine,
        emailContent,
        wikiItems: selectedItems,
        teamId,
        rawNotes,
        draftId
      }
    })
  }

  if (!teamId) {
    return null
  }

  return (
    <div className="min-h-screen bg-brand-cream py-8">
      <Container>
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h1 className="font-heading text-3xl font-bold mb-2">
              Review Your Dispatch
            </h1>
            <p className="text-brand-slate">
              Edit your email and approve wiki items before sending
            </p>
          </div>

          <div className="space-y-6">
            {/* Subject Line */}
            <Card>
              <Input
                label="Subject Line"
                value={subjectLine}
                onChange={(e) => setSubjectLine(e.target.value)}
                placeholder="Weekly Update"
              />
            </Card>

            {/* Email Content */}
            <Card>
              <div>
                <label className="label mb-2">Email Content</label>
                <DispatchEditor
                  content={emailContent}
                  onChange={setEmailContent}
                />
                <p className="mt-2 text-sm text-brand-slate">
                  Format the content below. What you see is what will be sent to families.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Button
                    variant="secondary"
                    onClick={handleSaveDraft}
                    disabled={savingDraft || !subjectLine || !emailContent}
                  >
                    {savingDraft ? 'Saving…' : 'Save Draft'}
                  </Button>
                  {lastSavedAt && (
                    <>
                      <span className="text-sm text-brand-slate">
                        Saved {lastSavedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </span>
                      <Button
                        variant="secondary"
                        onClick={() => navigate('/dashboard')}
                      >
                        ← Back to Dashboard
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>

            {/* Wiki Item Suggestions */}
            {wikiItems.length > 0 && (
              <WikiItemSuggestions
                items={wikiItems}
                selectedItems={selectedWikiItems}
                onToggle={toggleWikiItem}
              />
            )}

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
              <strong>Privacy check:</strong> Remove any sensitive personal data before sending. Only include information parents should see publicly.
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <Button
                variant="secondary"
                onClick={() => navigate('/dashboard', { state: { formState } })}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSendAndPublish}
                className="flex-1"
                disabled={!subjectLine || !emailContent}
              >
                Send & Publish →
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </div>
  )
}

export default DispatchReview

